import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authService } from '../services/api';

const ClinicSwitcher = () => {
  const { currentUser, clid, swapClinic } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [currentClinicName, setCurrentClinicName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Load available clinics and current clinic name
  useEffect(() => {
    const loadClinics = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get available clinics
        const response = await authService.getAvailableClinics();
        setAvailableClinics(response.clinics || []);

        // Get current clinic name from response or use clid as fallback
        if (response.current_clinic && response.current_clinic.name) {
          setCurrentClinicName(response.current_clinic.name);
        } else {
          setCurrentClinicName(clid);
        }
      } catch (err) {
        console.error('Error loading clinics:', err);
        setError(err.response?.data?.detail || 'Failed to load clinics');
        // If no clinics available, hide the component
        setAvailableClinics([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadClinics();
    }
  }, [currentUser, clid]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ESC key to close dropdown
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  const handleClinicSwap = async (clinicId) => {
    try {
      setLoading(true);
      setError(null);
      await swapClinic(clinicId);
      setIsOpen(false);
      // Refresh the page after successful swap
      window.location.reload();
    } catch (err) {
      console.error('Error swapping clinic:', err);
      setError(err.response?.data?.detail || 'Failed to swap clinic');
      setLoading(false);
    }
  };

  // Don't render if no available clinics
  if (!currentUser || availableClinics.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={t('common.clinic') || 'Clinic'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <span className="hidden sm:block max-w-[120px] truncate">
          {currentClinicName || clid}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50">
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b">
            {t('common.switchClinic') || 'Switch Clinic'}
          </div>
          {error && (
            <div className="px-4 py-2 text-sm text-red-600 border-b">
              {error}
            </div>
          )}
          <div className="py-1 max-h-64 overflow-y-auto" role="menu" aria-orientation="vertical">
            {availableClinics.map((clinic) => (
              <button
                key={clinic.id}
                onClick={() => handleClinicSwap(clinic.id)}
                disabled={loading}
                className="w-full flex items-start space-x-3 px-4 py-2 text-sm text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                role="menuitem"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{clinic.name}</div>
                  {clinic.description && (
                    <div className="text-xs text-gray-500 truncate">{clinic.description}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicSwitcher;

